import { GADataProps } from "@components/GAData/GAData";

type ClientGAData = {
  articleid: string;
  cue_articleid: string;
  at: string;
  author: string;
  chapter1: string;
  contentcat: string;
  contenttype: string;
  keyword: string;
  level2: string;
  pagination: string;
  permutive_id: string;
  pubdate: string;
  story_threads: string;
  title: string;
  visitorcat: string;
  termid: string;
  product_flag: string;
  user_keyword: string;
  adblocker: string;
  cd_version: string;
  gs_channels: string;
  event: string;
  abVariant: string;
  abVariant_user: string;
  language: string;
  svc: string;
};

type WindowWithPermutiveKeys = Window &
  typeof globalThis & {
    permutive_keys?: Record<string, unknown>;
  };

function getLocalStorageItem(key: string): string {
  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function getCookieByName(name: string): string {
  if (document.cookie.length === 0) return "";

  const cookie = document.cookie
    .split(";")
    .map((value) => value.trim())
    .find((value) => value.startsWith(`${name}=`));

  if (!cookie) return "";

  try {
    return decodeURIComponent(cookie.slice(name.length + 1));
  } catch {
    return "";
  }
}

function applyCookieOverrides(gaData: ClientGAData): void {
  const visitorCat = getCookieByName("visitorcat");
  const mysphw = getCookieByName("mysphw");

  if (visitorCat && mysphw) {
    gaData.visitorcat = visitorCat;
    gaData.at = mysphw;
  }

  const svc = getCookieByName("svc");

  if (svc) {
    gaData.svc = svc;
  }
}

function updateLegacyPermutiveKeys(gaData: ClientGAData): void {
  if (!gaData.articleid) return;

  const permutiveKeys = (window as WindowWithPermutiveKeys).permutive_keys;

  if (typeof permutiveKeys === "undefined") return;

  const keywordsMeta = document.querySelector<HTMLMetaElement>(
    "meta[name='keywords']"
  );

  if (!keywordsMeta) return;

  permutiveKeys.keywords = keywordsMeta.content
    .split(",")
    .map((value) => value.trim().toLowerCase());
  permutiveKeys.contentcat = "free";
  permutiveKeys.contenttype = "article";
  permutiveKeys.articleid = gaData.articleid;
}

/**
 * Updates GA data directly for client-side rendering.
 * Use this when GA data needs to be set dynamically after initial page load.
 */
export function injectClientGAData(gaData: GADataProps): void {
  if (typeof window === "undefined") return;

  const {
    articleid = "",
    at = "", // mysphw cookie
    author = "", // Comma separated author name
    chapter1 = "", // Section child category name.
    contentcat = 1, // Paid (2) or free (1)
    contenttype = "", // Index/listing = "", article = 1 photo = 2 video = 3
    keyword = "", // Comma separated keyword names
    // if in article, level2 = main category parent or main category, main category is the first category
    // if breakingnews, level2 = breaking news
    level2 = "",
    pagination = 1,
    // permutive_id = "", // window.localStorage.getItem("permutive-id") || "",
    pubdate = "", // Post date of article otherwise ""
    story_threads = "", // Comma separated story thread name
    title = "", // ie I am the article title, Section name, Breaking News
    visitorcat = 1,
    termid = "", // Section term id
    product_flag = "(not set)",
    user_keyword = "(not set)",
    cd_version = "bt2", // to determine if from pv is from bt1 or bt2, no value is bt1
    gsChannels = "", // grapeshot keywords
    event = "",
    abVariant = "",
    language = "en",
    adblocker = 0,
    svc = "",
  } = gaData;

  const clientGAData: ClientGAData = {
    articleid,
    cue_articleid: articleid,
    at,
    author,
    chapter1,
    contentcat: contentcat.toString(),
    contenttype,
    keyword,
    level2,
    pagination: pagination.toString(),
    permutive_id: getLocalStorageItem("permutive-id"),
    pubdate,
    story_threads,
    title,
    visitorcat: visitorcat.toString(),
    termid,
    product_flag,
    user_keyword,
    adblocker: adblocker.toString(),
    cd_version,
    gs_channels: gsChannels,
    event,
    abVariant,
    abVariant_user: abVariant,
    language,
    svc,
  };

  applyCookieOverrides(clientGAData);

  window._data = clientGAData;
  updateLegacyPermutiveKeys(clientGAData);
}
